import React from 'react'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  Alert,
  AlertTitle,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  type StyleProps,
} from '@mui/material'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import * as exportAPI from './export-api'
import { useExportJobs } from './export-items-state'
import { ExportJobBasic } from './export-queries'
import {
  commonExportStyles,
  ExportHeader,
  getProgress,
  getStatusLabel,
} from './export-result-common'
import { getErrorMessage } from './export-utils'

const styles = {
  tabTitle: {
    typography: 'h5',
    mt: 2,
    mb: 4,
    px: 3,
  },
  ...commonExportStyles,
  table: {
    mt: 2,
    '& td, & th': {
      py: 1,
    },
    '& tbody td': {
      borderBottom: 0,
    },
    '& tbody tr:last-of-type td': {
      borderBottom: '1px solid',
      borderColor: 'divider',
    },
    '& tbody tr:nth-of-type(odd)': {
      bgcolor: 'background.grey',
    },
  },
} satisfies StyleProps

const getDeliveryStatusLabel = (status: string, t: TFunction<'export'>) => {
  const labelMap: Record<string, string> = {
    success: t('webhook.delivery_status.success'),
    failed: t('webhook.delivery_status.failed'),
    in_progress: t('webhook.delivery_status.in_progress'),
    pending: t('webhook.delivery_status.pending'),
    retrying: t('webhook.delivery_status.retrying'),
  }

  return labelMap[status] || status
}

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'

const getDeliveryStatusColor = (status: string): ChipColor => {
  const colorMap: Record<string, ChipColor> = {
    success: 'success',
    failed: 'error',
    in_progress: 'info',
    pending: 'warning',
    retrying: 'warning',
  }

  return colorMap[status] ?? 'default'
}

type WebhookExportJobRowProps = {
  jobBasic: ExportJobBasic
  isExpanded: boolean
}

const WebhookExportJobRow = ({ jobBasic, isExpanded }: WebhookExportJobRowProps) => {
  const { t } = useTranslation('export')
  const [updateDialog, isChecked] = useExportJobs((state) => [
    state.updateDialog,
    state.exportDialog.selectedJobIds.has(jobBasic.id),
  ])

  const exportDetails = exportAPI.useGetExportDetails(jobBasic.id, isExpanded)

  const webhookDeliveries = exportAPI.useListWebhookDeliveries(jobBasic.id, isExpanded)

  const exportJob = exportDetails.data?.exportJob
  const deliveries = webhookDeliveries.data || []

  const status = exportJob?.status || jobBasic.status
  const isFailed = status === 'failed'
  const isCompleted = status === 'completed'
  const isProcessing = status === 'in_progress' || status === 'pending'
  const hasDeliveryAttempts = deliveries.length > 0

  const showContent = Boolean(isExpanded && exportDetails.data && !exportDetails.isLoading)
  const showError = Boolean(
    isExpanded && isFailed && !hasDeliveryAttempts && !exportDetails.isLoading,
  )
  const exportLabel = jobBasic.webhookDeliveryAttempts?.[0]?.webhookConfig?.label
  const handleCheck = () => {
    if (isChecked) {
      updateDialog((state) => {
        state.selectedJobIds.delete(jobBasic.id)
      })
    } else {
      updateDialog((state) => {
        state.selectedJobIds.add(jobBasic.id)
      })
    }
  }

  const progress = getProgress(exportJob)

  return (
    <Box my={2} bgcolor="background.grey" p={2} mx={3} borderRadius={2}>
      <Box aria-label="export-job-header" sx={styles.exportJobHeader}>
        <Checkbox onChange={handleCheck} checked={isChecked} color="secondary" size="small" />
        <ExportHeader job={jobBasic} showBadge={isFailed} badgeColor="error" />
        <IconButton
          disabled={exportDetails.isLoading}
          onClick={() =>
            updateDialog({
              selectedJobId: isExpanded ? '' : jobBasic.id,
            })
          }
        >
          {isExpanded ? (
            exportDetails.isLoading ? (
              <CircularProgress size={20} color="secondary" />
            ) : (
              <ExpandLess />
            )
          ) : (
            <ExpandMore />
          )}
        </IconButton>
      </Box>

      <Collapse in={showError} timeout="auto" unmountOnExit>
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>{getErrorMessage(exportJob?.errorDetails).title}</AlertTitle>
          {getErrorMessage(exportJob?.errorDetails).message}
        </Alert>
      </Collapse>

      <Collapse in={showContent} timeout="auto" unmountOnExit>
        <Box mt={2}>
          {isProcessing && (
            <Box mb={3}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                typography="h5"
                sx={{ mb: 1 }}
              >
                <span>{getStatusLabel(status, t)}</span>
                <span>{progress.toFixed(1)}%</span>
              </Box>
              <LinearProgress
                value={progress}
                variant={exportJob?.totalItems ? 'determinate' : 'indeterminate'}
                color="secondary"
              />
              {!!exportJob?.totalItems && (
                <Box typography="h6" mt={1}>
                  {t('item_count', { count: exportJob.totalItems })}
                </Box>
              )}
            </Box>
          )}

          {/* Webhook Export Status */}
          {(isCompleted || (isFailed && hasDeliveryAttempts)) && (
            <Box>
              <Typography variant="h6" mb={2}>
                {t('webhook.status')}
              </Typography>

              {webhookDeliveries.isLoading && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">{t('webhook.loading_status')}</Typography>
                </Box>
              )}

              {!webhookDeliveries.isLoading && deliveries.length === 0 && (
                <Alert severity="info">{t('webhook.no_attempts_found')}</Alert>
              )}

              {!webhookDeliveries.isLoading && !isFailed && deliveries.length > 0 && (
                <Alert severity="success">
                  {t('webhook.request_received_by', { label: exportLabel })}
                </Alert>
              )}

              {!webhookDeliveries.isLoading && isFailed && deliveries.length > 0 && (
                <Table sx={styles.table} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('webhook.attempt')}</TableCell>
                      <TableCell>{t('webhook.status')}</TableCell>
                      <TableCell>HTTP</TableCell>
                      <TableCell>{t('webhook.error')}</TableCell>
                      <TableCell>{t('webhook.attempted')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deliveries.map((delivery) => (
                      <TableRow key={delivery.id}>
                        <TableCell>#{delivery.attemptNumber}</TableCell>
                        <TableCell>
                          <Chip
                            label={getDeliveryStatusLabel(delivery.status, t)}
                            color={getDeliveryStatusColor(delivery.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{delivery.httpStatusCode || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 300 }}>
                          <Typography variant="body2" noWrap>
                            {delivery.errorMessage || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {new Date(delivery.attemptedAt).toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {!!exportJob?.totalItems && (
                <Box typography="h6" mt={2}>
                  {t('item_count_exported', { count: exportJob.totalItems })}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

export default WebhookExportJobRow
